import moment from 'moment';
import get from 'lodash/get';
import { MENU as ENTITY } from 'menus/constants/entities';
import { getItemByIdSelector } from 'base/reducers/bentoApi';
import { generatePath } from 'utils/generatePath';
import { getOnlineOrderingUrlParams } from 'utils/url';
import { getAreRequiredFieldsSelected } from 'menus/utils/addToCartCta';
import { getMenuItem } from 'menus/reducers/menuItemsDetail';
import { getQuantityOfProductInCart } from 'cart/selectors';
import { getProductsWithInventoryForLocation } from 'locations/reducers/locationInventory';
import { combineDateWithCloseTime } from 'base/utils/timeHelpers';
import { throwMenuNotFoundError } from 'base/utils/errorHelpers';
import {
  getLocationMenuForLaterOrNextSelector,
  getLocationMenuForAsapOrFirstLaterSelector
} from 'locations/selectors';

// called on root state
export const getIsEnabledAddToCartSelector = state => {
  const { form, menuItemsDetail, router, orderAmount } = state;
  const { menuItemSlug } = getOnlineOrderingUrlParams(router.location.pathname);
  const menuItem = getMenuItem(menuItemsDetail, menuItemSlug);
  const values = get(form, 'menuItemDetail.values', {});
  const fields = get(menuItem, 'variants[0].fields', []);
  const requiredFieldsAreSelected = getAreRequiredFieldsSelected(
    values,
    fields
  );

  // we assume an item is available unless is available has set to false
  const isMenuItemAvailable = menuItem.is_available !== false;
  const isMaximumExceeded = orderAmount.isMaximumExceeded;
  return {
    requiredFieldsAreSelected,
    isMaximumExceeded,
    isMenuItemAvailable,
    canAddToCart: !!(
      requiredFieldsAreSelected &&
      !isMaximumExceeded &&
      isMenuItemAvailable
    )
  };
};

/**
  get if inventory applies to the product given pwi and requested_fulfillment_is_same_day
  @param {object} pwi (product with inventory object)
  @param {object} cartState (cart object)
  @return {boolean} representing if inventory should count on
  for menu given fulfillment options and pwi date/count future fields
*/
export const getDoesInventoryApply = (pwi, cartState) => {
  const requestedFulfillmentIsSameDay =
    cartState.requested_fulfillment_is_same_day;

  const shouldCountInventoryForFulfillmentDate =
    pwi.count_future_day_orders || requestedFulfillmentIsSameDay;

  const inventoryIsLowAtFulfillmentDatetime =
    pwi.inventory !== null &&
    (pwi.reset_time === null ||
      moment
        .utc(pwi.reset_time)
        .isAfter(moment.utc(cartState.requested_fulfillment_datetime_as_utc)));

  return (
    shouldCountInventoryForFulfillmentDate &&
    inventoryIsLowAtFulfillmentDatetime
  );
};

/**
 * get inventory for product of id
  @param {object} productsWithInventory (product with inventory object)
  @param {number} productId
  @param {object} cartState (cart object)
  @return {number} representing available inventory (or null if inventory does not apply)
*/
export const getInventoryForProductId = (
  productsWithInventory,
  productId,
  cartState
) => {
  const product = productsWithInventory.find(pwi => {
    return pwi.product === productId;
  });
  if (!product) {
    return null;
  }
  const doesInventoryApply = selectorsModule.getDoesInventoryApply(
    product,
    cartState
  );
  return doesInventoryApply ? product.inventory : null;
};

/**
 * get inventory for item
 * @param {object} item (menuItem obj)
 * @param {array} productsWithInventory (arr of pwi objects)
 * @param {array} soldOutProducts (arr of Sold Out product ids (integers))
 * @param {object} cart (cart object)
 * @return {boolean} representing if inventory should count on
 * for menu given fulfillment options and pwi date/count future fields

 * process item to return Sold Out status and inventory values
*/
export const getItemWithInventorySelector = (
  item,
  productsWithInventory,
  cart
) => {
  const inventory = selectorsModule.getInventoryForProductId(
    productsWithInventory,
    item.product.id,
    cart
  );
  let inventoryLessCart = null;
  if (inventory !== null) {
    const qtyInCart = getQuantityOfProductInCart(cart, item.product.id);
    inventoryLessCart = inventory - qtyInCart;
  }

  return {
    ...item,
    inventory,
    inventoryLessCart
  };
};

/**
 * get menu with inventory for all items
  @param {object} state (root of redux state)

  @return {object} menu with items combined with inventory
*/
export const getMenuWithInventorySelector = state => {
  const productsWithInventory =
    getProductsWithInventoryForLocation(
      state.locationInventory,
      state.cart.online_ordering_location
    ) || [];

  return {
    ...state.menu,
    sections: (state.menu.sections || []).map(section => ({
      ...section,
      items: section.items.map(item =>
        selectorsModule.getItemWithInventorySelector(
          item,
          productsWithInventory,
          state.cart
        )
      )
    }))
  };
};

/**
 * getBaseMenuPathForLocation
  @param {object} state (root of redux state)

  @return {string} path of menu (defaults to root if location not found)
*/
export const getBaseMenuPathForLocation = state => {
  const { cart, restaurantLocations } = state;
  const id = cart.online_ordering_location;
  const selectedLocationSlug = get(
    getItemByIdSelector(restaurantLocations, id),
    'location.slug'
  );
  if (selectedLocationSlug) {
    return generatePath(ENTITY, 'menu', { slug: selectedLocationSlug });
  } else {
    return '/';
  }
};

/**
 * Takes global state, and returns time the currently active menu expires
 * Used to determine when a menu has expired
 * @param {object} state global state object
 *
 * @returns {string} representing the datetime of when menu expires
 */
export const getLocationMenuCloseDateTimeSelector = state => {
  let locationMenu = null;
  // double check that cart is always valid here vs workingCopy
  const { cart, restaurantLocations, locationThrottledTimes } = state;

  if (cart.requested_fulfillment_is_asap) {
    locationMenu = getLocationMenuForAsapOrFirstLaterSelector(
      restaurantLocations,
      cart,
      locationThrottledTimes
    );
  } else {
    // may need to add location throttled times here
    locationMenu = getLocationMenuForLaterOrNextSelector(
      restaurantLocations,
      cart
    );
  }
  const closeTime = locationMenu
    ? combineDateWithCloseTime(locationMenu.date, locationMenu.close_time)
    : '';

  return closeTime;
};

/**
 * Takes state for restaurantLocations and cart and returns the id of the
 * active menu based on the cart's fulfillment options.
 * Used to fetch menu and menu details
 * @param {object} restaurantLocations restaurantLocations state object
 * @param {object} cart cart state object
 * @param {object} locationThrottledTimes from state
 * @returns {number} menu id
 */
export const getMenuIdForFulfillmentOptions = (
  restaurantLocations,
  cart,
  locationThrottledTimes
) => {
  let menuId = null;
  if (cart.requested_fulfillment_is_asap) {
    const locationMenu =
      getLocationMenuForAsapOrFirstLaterSelector(
        restaurantLocations,
        cart,
        locationThrottledTimes
      ) || {};

    menuId = locationMenu.menu_id;
  } else {
    const locationMenu =
      getLocationMenuForLaterOrNextSelector(restaurantLocations, cart) || {};
    menuId = locationMenu.menu_id;
  }

  // throw an error if no menu found so this is captured in sentry
  if (!menuId) {
    const fulfillmentOption = cart.requested_fulfillment_is_asap
      ? 'ASAP'
      : 'Later';
    const msg = `No menu found for cart with slug: ${cart.slug}, fulfillmentOption: ${fulfillmentOption}, fulfillmentTime: ${cart.fulfillment_time}, fulfillmentDate: ${cart.fulfillment_date}`;
    throwMenuNotFoundError(msg);
  }

  return menuId;
};

// using this pattern to be able to easily mock parts of the module
// in jest, but that means any functions in here that use
// other functions defined below should use the exported module version
const selectorsModule = {
  getIsEnabledAddToCartSelector,
  getDoesInventoryApply,
  getInventoryForProductId,
  getMenuWithInventorySelector,
  getBaseMenuPathForLocation,
  getItemWithInventorySelector,
  getLocationMenuCloseDateTimeSelector,
  getMenuIdForFulfillmentOptions
};

export default selectorsModule;
